import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { ghs } from '../lib/money.js';
import { Button, Chip, Banner } from '../components/ui.jsx';
import { Sheet, Divider, Section } from './SalesPoint.jsx';

const OPEN_HOUR = 9;
const HOURS = 9;                 // 09:00 to 18:00
const PX_PER_HOUR = 80;
const px = mins => (mins * PX_PER_HOUR) / 60;

const TONE = {
  done: 'success', in_progress: 'accent', arrived: 'warning',
  booked: 'neutral', no_show: 'danger', cancelled: 'danger'
};
const LABEL = {
  done: 'Done', in_progress: 'In progress', arrived: 'Arrived',
  booked: 'Booked', no_show: 'No show', cancelled: 'Cancelled'
};

export function Appointments({ slug, onCheckedIn }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [booking, setBooking] = useState(false);
  const [open, setOpen] = useState(null);

  const load = () => b.appointments(date).then(setData).catch(() => {});
  useEffect(() => { load(); }, [date, slug]);
  if (!data) return <div className="t-body-m muted">Loading the diary</div>;

  const shift = days => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().slice(0, 10));
  };

  const active = data.appointments.filter(a => a.status !== 'cancelled');

  return (
    <div style={{ margin: -24, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', height: 64, flex: 'none',
        background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border-default)'
      }}>
        <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }} onClick={() => shift(-1)}>Back</Button>
        <span className="t-heading-m">{longDate(date)}</span>
        <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }} onClick={() => shift(1)}>Next</Button>
        <Chip tone="neutral" showDot={false}>{active.length} appointment{active.length === 1 ? '' : 's'}</Chip>
        <span style={{ flex: 1 }} />
        {['done', 'in_progress', 'arrived', 'booked'].map(s => (
          <Chip key={s} tone={TONE[s]}>{LABEL[s]}</Chip>
        ))}
        <Button size="m" onClick={() => setBooking(true)}>New appointment</Button>
      </div>

      {/* staff column headers */}
      <div style={{
        display: 'flex', flex: 'none', height: 44, background: 'var(--color-bg-subtle)',
        borderBottom: '1px solid var(--color-border-default)'
      }}>
        <div style={{ width: 72, flex: 'none' }} />
        {data.staff.map(s => (
          <div key={s.id} style={{
            flex: 1, display: 'grid', placeItems: 'center',
            borderLeft: '1px solid var(--color-border-default)'
          }} className="t-heading-s">{s.name}</div>
        ))}
      </div>

      {/* the grid */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ display: 'flex', minHeight: HOURS * PX_PER_HOUR }}>
          <div style={{ width: 72, flex: 'none' }}>
            {Array.from({ length: HOURS }, (_, i) => (
              <div key={i} style={{
                height: PX_PER_HOUR, borderTop: '1px solid var(--color-border-default)',
                display: 'flex', justifyContent: 'flex-end', paddingRight: 8
              }} className="t-body-s muted">
                {String(OPEN_HOUR + i).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {data.staff.map(s => (
            <Column key={s.id} staff={s} appointments={active.filter(a => a.staff_id === s.id)} onOpen={setOpen} />
          ))}
        </div>
      </div>

      {booking && (
        <NewAppointment slug={slug} date={date} staff={data.staff}
          onClose={() => setBooking(false)}
          onBooked={() => { setBooking(false); load(); }} />
      )}
      {open && (
        <AppointmentDetail slug={slug} appointment={open}
          onClose={() => setOpen(null)}
          onChanged={() => { setOpen(null); load(); }}
          onCheckedIn={cart => { setOpen(null); load(); onCheckedIn?.(cart); }} />
      )}
    </div>
  );
}

function Column({ staff, appointments, onOpen }) {
  return (
    <div style={{
      flex: 1, position: 'relative', borderLeft: '1px solid var(--color-border-default)', minWidth: 0
    }}>
      {Array.from({ length: HOURS }, (_, i) => (
        <div key={i} style={{ height: PX_PER_HOUR, borderTop: '1px solid var(--color-border-default)' }} />
      ))}
      {appointments.map(a => {
        const start = new Date(a.starts_at);
        const fromOpen = (start.getHours() - OPEN_HOUR) * 60 + start.getMinutes();
        const tone = TONE[a.status];
        return (
          <button key={a.id} onClick={() => onOpen(a)}
            data-testid="appointment" data-name={a.customer_name}
            style={{
              position: 'absolute', left: 6, right: 6, top: px(fromOpen), height: Math.max(28, px(a.duration_min) - 3),
              textAlign: 'left', padding: '6px 8px', overflow: 'hidden', cursor: 'pointer',
              borderRadius: 'var(--radius-sm)', border: 0,
              borderLeft: `3px solid var(--color-status-${tone === 'accent' ? 'info' : tone})`,
              background: tone === 'accent' ? 'var(--color-accent-subtle)' : `var(--color-status-${tone}-bg)`,
              color: tone === 'accent' ? 'var(--color-accent-strong)' : `var(--color-status-${tone})`,
              font: 'inherit'
            }}>
            <div className="t-heading-s" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {a.customer_name}
            </div>
            <div className="t-body-s" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {a.service_name}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function NewAppointment({ slug, date, staff, onClose, onBooked }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [cat, setCat] = useState({ services: [] });
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customerId: null, customerName: '', serviceId: null, staffId: null, time: '10:00', durationMin: null
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    b.catalogue().then(setCat).catch(() => {});
    b.customers().then(d => setCustomers(d.customers)).catch(() => {});
  }, [slug]);

  const service = cat.services.find(s => s.id === form.serviceId);
  const mins = form.durationMin ?? service?.duration_min ?? 60;

  // Only staff whose speciality covers the chosen service can be booked for it.
  const [able, setAble] = useState(staff);
  useEffect(() => {
    if (!form.serviceId) { setAble(staff); return; }
    b.staff(form.serviceId).then(d => {
      setAble(d.staff);
      // If the person already picked cannot do it, drop them rather than fail on save.
      if (form.staffId && !d.staff.some(s => s.id === form.staffId)) {
        setForm(f => ({ ...f, staffId: null }));
      }
    }).catch(() => setAble(staff));
  }, [form.serviceId, staff]);

  async function book() {
    setError(null);
    try {
      await b.createAppointment({
        customerId: form.customerId, customerName: form.customerName || 'Walk-in',
        serviceId: form.serviceId, staffId: form.staffId,
        startsAt: new Date(`${date}T${form.time}:00`).toISOString(),
        durationMin: mins, source: 'counter'
      });
      onBooked();
    } catch (e) {
      if (e.data?.error === 'staff_double_booked') {
        setError(`That stylist already has ${e.data.clash.customer} from ${time(e.data.clash.from)} to ${time(e.data.clash.to)}. A stylist cannot be in two places.`);
      } else if (e.data?.error === 'not_their_speciality') {
        const who = (e.data.canDoIt || []).map(s => s.name).join(', ');
        setError(`${e.data.staffName} does not do ${e.data.categoryName}.` + (who ? ` ${who} can.` : ''));
      } else {
        setError('That booking could not be saved.');
      }
    }
  }

  return (
    <Sheet title="New appointment" subtitle="Duration fills in from the service. Change it only if this booking is unusual." onClose={onClose}>
      <Section label="CUSTOMER">
        <select value={form.customerId ?? ''} onChange={e => {
          const id = e.target.value ? Number(e.target.value) : null;
          const c = customers.find(x => x.id === id);
          setForm(f => ({ ...f, customerId: id, customerName: c?.name || '' }));
        }} style={selectStyle}>
          <option value="">Walk-in, no record</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}
        </select>
      </Section>
      <Divider />
      <Section label="SERVICE">
        <select value={form.serviceId ?? ''} onChange={e => setForm(f => ({ ...f, serviceId: Number(e.target.value) || null, durationMin: null }))}
          style={selectStyle}>
          <option value="">Choose a service</option>
          {cat.services.map(s => (
            <option key={s.id} value={s.id}>{s.name} · {ghs(s.price)}{s.duration_min ? ` · ${s.duration_min} min` : ''}</option>
          ))}
        </select>
      </Section>
      <Divider />
      <Section label={form.serviceId ? 'STAFF WHO DO THIS' : 'STAFF'}>
        {form.serviceId && !able.length && (
          <Banner tone="danger" title="Nobody is set up to perform this">
            Add the category to whoever does it on the Staff screen, then book it here.
          </Banner>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {able.map(s => {
            const on = form.staffId === s.id;
            return (
              <button key={s.id} onClick={() => setForm(f => ({ ...f, staffId: on ? null : s.id }))}
                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
                <Chip tone={on ? 'accent' : 'neutral'} size="m" showDot={on}>{s.name}</Chip>
              </button>
            );
          })}
        </div>
        {form.serviceId && staff.length > able.length && (
          <div className="t-body-s muted">
            {staff.length - able.length} other staff are hidden because this is not their speciality.
          </div>
        )}
      </Section>
      <Divider />
      <Section label="WHEN">
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ flex: 1 }}>
            <div className="t-body-s muted" style={{ marginBottom: 4 }}>Time</div>
            <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
              style={{ ...selectStyle, width: '100%' }} />
          </label>
          <label style={{ flex: 1 }}>
            <div className="t-body-s muted" style={{ marginBottom: 4 }}>Duration, minutes</div>
            <input type="number" value={mins} onChange={e => setForm(f => ({ ...f, durationMin: Number(e.target.value) }))}
              style={{ ...selectStyle, width: '100%' }} />
          </label>
        </div>
        <div className="t-body-s muted">{longDate(date)} at {form.time}, ending {endTime(form.time, mins)}.</div>
      </Section>
      {error && <div style={{ padding: '0 24px' }}><Banner tone="danger">{error}</Banner></div>}
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button style={{ flex: 1 }} disabled={!form.serviceId} onClick={book}>Book appointment</Button>
      </div>
    </Sheet>
  );
}

function AppointmentDetail({ slug, appointment: a, onClose, onChanged, onCheckedIn }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const set = status => b.setAppointmentStatus(a.id, status).then(onChanged).catch(() => {});

  return (
    <Sheet title={a.customer_name} subtitle={`${time(a.starts_at)} to ${time(a.ends_at)} · ${a.staff_name || 'nobody assigned'}`} onClose={onClose}>
      <Section label="APPOINTMENT">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Chip tone={TONE[a.status]}>{LABEL[a.status]}</Chip>
          <Chip tone="neutral" showDot={false}>{a.source === 'web' ? 'Booked online' : 'Booked at the counter'}</Chip>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 8 }}>
          <div style={{ flex: 1 }}>
            <div className="t-body-l">{a.service_name}</div>
            <div className="t-body-s muted">{a.duration_min} min · {a.staff_name || 'unassigned'}</div>
          </div>
          <span className="money t-money-l">{ghs(a.price ?? 0)}</span>
        </div>
      </Section>
      <Divider />
      <Section label="MOVE IT ON">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {a.status === 'booked' && <Button size="m" variant="secondary" onClick={() => set('arrived')}>Mark arrived</Button>}
          {a.status !== 'done' && <Button size="m" variant="secondary" onClick={() => set('done')}>Mark done</Button>}
          {a.status === 'booked' && <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }} onClick={() => set('no_show')}>No show</Button>}
          <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }} onClick={() => set('cancelled')}>Cancel</Button>
        </div>
      </Section>
      <div style={{ flex: 1 }} />
      <div style={{ padding: '16px 24px', background: 'var(--color-status-info-bg)', color: 'var(--color-status-info)' }} className="t-body-s">
        Checking in opens the cart with this service and stylist already on it. One tap, no retyping.
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button style={{ flex: 1 }} onClick={() => b.checkIn(a.id).then(r => onCheckedIn(r.cart)).catch(() => {})}>
          Check in and open cart
        </Button>
      </div>
    </Sheet>
  );
}

const selectStyle = {
  height: 48, padding: '0 12px', width: '100%',
  background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)',
  font: '400 15px/22px var(--font-ui)'
};

const time = t => new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const longDate = d => new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
function endTime(hhmm, mins) {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h, m + mins);
  return d.toTimeString().slice(0, 5);
}
